import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AuthLayout from '../components/AuthLayout';
import userService from '../services/user.service';
import { UserType } from '../types/user.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Mapeamento de perfis para exibição em português
const roleLabels: Record<string, string> = {
  'admin': 'Administrador',
  'manager': 'Comercial',
  'field-agent': 'Técnico',
  'user': 'Usuário'
};

// Cores para os chips de perfil
const roleColors: Record<string, string> = {
  'admin': 'error',
  'manager': 'primary',
  'field-agent': 'success',
  'user': 'default'
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      try {
        const userData = await userService.getUserById(id);
        setUser(userData);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar dados do usuário');
        console.error('Erro ao carregar dados do usuário:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // Função para formatar data
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return 'N/A';
    try {
      // Se já for um objeto Date, usar diretamente
      const dateObj = date instanceof Date ? date : new Date(date);
      return format(dateObj, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  return (
    <AuthLayout title="Detalhes do Usuário">
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/users')}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
            Voltar para lista de usuários
          </MuiLink>
          <Typography color="text.primary">Detalhes do Usuário</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Detalhes do Usuário
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/users/edit/${id}`)}
          >
            Editar
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : user ? (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Cabeçalho com Avatar e Informações Básicas */}
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={user.photoURL || undefined}
                  sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                >
                  {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h5" component="h2">
                    {user.displayName || 'Sem nome'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip 
                      label={roleLabels[user.role] || user.role} 
                      color={roleColors[user.role] as any || 'default'}
                      size="small"
                    />
                    <Chip
                      label={user.isActive ? 'Ativo' : 'Inativo'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Informações Detalhadas */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EmailIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    Email:
                  </Typography>
                  <Typography variant="body1" component="span">
                    {user.email}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    ID Firebase:
                  </Typography>
                  <Typography variant="body1" component="span" sx={{ wordBreak: 'break-all' }}>
                    {user.uid}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WorkIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    Perfil:
                  </Typography>
                  <Typography variant="body1" component="span">
                    {roleLabels[user.role] || user.role}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    Último Login:
                  </Typography>
                  <Typography variant="body1" component="span">
                    {user.updatedAt ? formatDate(user.updatedAt) : 'Nunca'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    Criado em:
                  </Typography>
                  <Typography variant="body1" component="span">
                    {user.createdAt ? formatDate(user.createdAt.toString()) : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" />
                  <Typography variant="body1" component="span" fontWeight="bold">
                    Atualizado em:
                  </Typography>
                  <Typography variant="body1" component="span">
                    {user.updatedAt ? formatDate(user.updatedAt.toString()) : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              {/* Removido bloco de metadados que não existe no tipo UserType */}
            </Grid>
          </Paper>
        ) : (
          <Alert severity="info">Usuário não encontrado</Alert>
        )}
      </Box>
    </AuthLayout>
  );
};

export default UserDetail;
